import { Fragment, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import {
  GET_SEARCH_STOCK,
  PATCH_CREATE_SELLER_PRODUCT,
  POST_UPDATE_STOCK_STATUS,
} from "../../../../apis/configuration";
import { AppPagination, ParagraphText, UBButton } from "../../../../Libraries";
import { FilterNonIdeal, PopoverMenu } from "../../../common";
import HeaderItem from "./HeaderItem";
import ListItem from "./ListItem";

import { analytics, triggerLogEvent } from "../../../../config/firebase";
import AppTableFilters from "../../../common/AppTableFilters";
import { TABLE_FILTERS_STOCK_MANAGEMENT } from "../../../../constants";
import { buildFiltersConfig } from "../../../../utils";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import { DownNewIcon, TickIcon } from "../../../../assets/icons/icon";
import styles from "./styles.module.css";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";
import { handleApi } from "../../../../apis";

const statusOptions = [
  { label: "In stock", id: 1, value: "in-stock" },
  { label: "Out of stock", id: 2, value: "out-of-stock" },
];

const StockList = ({ data }) => {
  const {
    toggleStockTypeModal,
    handlePagination,
    handleStockDetails,
    handleStock,
    stockCount,
    pageLoad,
    list,
    I18n,
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
    handleGetProducts,
    isAppliedFilter,
  } = data || {};

  const [publishModal, setPublishModal] = useState(false);
  const [stockModal, setstockModal] = useState(false);
  const [publishList, setPublisList] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Receive params of id and list to select and deselect items in list individually or all
   * @returns Items to publish
   */

  const handleSelection = (id, list = false) => {
    if (list) {
      if (list.length > 15) {
        toast.error("You can only select up to 15 items", {
          className: "errorToast",
        });
        return false;
      }
      if (list.length !== Object.keys(publishList).length) {
        setPublisList((prev) => {
          let all = { ...prev };
          list.forEach((val) => {
            all[val.id] = { publish: true, id: val.id };
          });
          return all;
        });
      } else {
        setPublisList({});
      }
    } else {
      const newList = { ...publishList };
      const alreadySelected = !!newList[id];
      const currentCount = Object.keys(newList).length;

      if (!alreadySelected && currentCount >= 15) {
        toast.error("You can only select up to 15 items", {
          className: "errorToast",
        });
        return false;
      }
      if (alreadySelected) {
        delete newList[id];
      } else {
        newList[id] = { publish: true, id };
      }
      setPublisList(newList);
    }
  };

  /**
   * Calling an api to publish stock in bulk
   * @returns succcess or error of publish stock
   */

  const handleBulkPublish = () => {
    setLoading(true);

    let params = {
      publish: true,
      product_id: Object.keys(publishList),
    };
    handleStock(PATCH_CREATE_SELLER_PRODUCT, params)
      .then(() => {
        togglePublishModal();
        toast.success("Stocks publish successfully", {
          className: "successToast",
        });

        if (Object.keys(publishList).length === 1) {
          triggerLogEvent(analytics, "sc_stock_publish", {});
        } else {
          triggerLogEvent(analytics, "sc_stock_bulk_publish", {});
        }
        setPublisList({});
      })
      .finally(() => setLoading(false));
  };

  /**
   * Toggle publish modal by toggle value
   * @returns true or false
   */
  const togglePublishModal = (toggle = false) => setPublishModal(toggle);

  /**
   * Toggle stock modal by toggle value
   * @returns true or false
   */
  const toggleStockModal = (toggle = false) => setstockModal(toggle);

  /**
   * Calling an api to publish stock in bulk
   */

  const handleBulkUpdateStatus = () => {
    setLoading(true);

    const product_info = Object.keys(publishList).map((key) => ({
      product_id: Number(key),
      variant_ids: [],
    }));
    handleStock(POST_UPDATE_STOCK_STATUS, {
      action: selectedStatus?.value,
      product_info,
    })
      .then(() => {
        toast.success("Stock status updated successfully", {
          className: "successToast",
        });
        if (Object.keys(publishList).length === 1) {
          triggerLogEvent(analytics, "sc_stock_status", {});
        } else {
          triggerLogEvent(analytics, "sc_stock_bulk_status", {});
        }

        setSelectedStatus(null);
        setPublisList({});
        toggleStockModal();
      })
      .finally(() => setLoading(false));
  };

  const customButtons = [
    {
      disabled: Object.values(publishList).length ? false : true,
      loading: false,
      action: () => {
        togglePublishModal(true);
      },
      title: I18n.publish,
      className: styles.printButton,
      iconComponent: <TickIcon width={"12px"} height="12px" />,
    },
  ];

  const actionsComponent = () => {
    return (
      <Box className={styles.customButtonWrap}>
        {customButtons.length > 0
          ? customButtons.map((item) => (
              <UBButton
                id="stock_list_bulk_action_btn"
                title={item.title}
                onClick={item.action}
                disabled={item.disabled}
                spinnerColor={item.spinnerColor}
                iconPlacement={item?.iconPlacement}
                iconComponent={item.loading ? null : item.iconComponent}
                className={item.className}
                style={item.style}
                loading={item.loading}
              />
            ))
          : null}
        <PopoverMenu
          id="stock_management_popover"
          menuOptions={[
            {
              label: (
                <Fragment>
                  <ParagraphText className="textCapitalizeFirstLetter">
                    {`${I18n.in_stock}`}
                  </ParagraphText>
                </Fragment>
              ),
              action: (e) => {
                e.stopPropagation();
                setSelectedStatus({
                  firstLetter: "I",
                  id: 1,
                  label: "In stock",
                  value: "in-stock",
                });
                toggleStockModal(true);
              },
            },
            {
              label: (
                <Fragment>
                  <ParagraphText className="textCapitalizeFirstLetter">
                    {`${I18n.out_of_stock}`}
                  </ParagraphText>
                </Fragment>
              ),
              action: (e) => {
                e.stopPropagation();
                setSelectedStatus({
                  firstLetter: "O",
                  id: 2,
                  label: "Out of stock",
                  value: "out-of-stock",
                });
                toggleStockModal(true);
              },
            },
          ]}
          popOverId="mark_stock_status"
          popOverStyle={{
            marginLeft: "0px",
            borderRadius: "16px !important",
          }}
          popoverButtonClassName={styles.printButton}
          popoverButton={I18n.actions}
          disabled={Object.values(publishList).length ? false : true}
          popoverButtonRestProps={{
            iconRightSideComponent: (
              <Box className="marginLeft5 marginTop5">
                <DownNewIcon />
              </Box>
            ),
          }}
          openPopoverClassName={styles.openPopoverClassName}
        />
      </Box>
    );
  };

  return (
    <>
      <AppTableFilters
        filters={TABLE_FILTERS_STOCK_MANAGEMENT}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => {
          if (isAppliedFilter) {
            handleGetProducts();
          }
          handleRemoveChip(chip);
        }}
        filtersConfig={buildFiltersConfig(TABLE_FILTERS_STOCK_MANAGEMENT, [], [
            { label: I18n.in_stock, value: "In stock" },
            { label: I18n.out_of_stock, value: "Out of stock" },
          ], null, "Search by item name", null, null, null, "sort", I18n)}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
        children={actionsComponent()}
      />

      <div className={"orderManagementInnerBox"}>
        {list.length ? (
          <Box
            className={`${"accountListBodyWarpper"} generic-scrollbar`}
            id="scrollableDiv"
            style={{ width: "100%" }}
          >
            <div className={styles.stickyHeader}>
              <HeaderItem data={{ handleSelection, publishList, list, I18n }} />
            </div>
            <AppPagination
              data={{
                handleNext: handlePagination,
                count: stockCount,
                pageLoad,
                list,
              }}
            >
              {list.map((stock, index) => (
                <ListItem
                  data={{
                    handleStockDetails,
                    handleSelection,
                    handleStock,
                    publishList,
                    stock,
                    index,
                    I18n,
                  }}
                />
              ))}
            </AppPagination>
          </Box>
        ) : (
          <>
            <HeaderItem
              data={{ handleSelection, publishList, list: [], I18n }}
            />

            <Box className="h-100 w-100">
              <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={I18n.try_adjusting_filters}
                subTitleTextOnClick={() => {
                  if (isAppliedFilter) {
                    handleGetProducts();
                  }
                  resetFilters();
                }}
                subTitleText={I18n.reset_small}
              />
            </Box>
          </>
        )}
      </div>

      <ConfirmationModal
        open={publishModal}
        onClose={() => togglePublishModal(false)}
        onConfirm={handleBulkPublish}
        message={`Would you like to publish the selected items on your catalog?`}
        confirmText={I18n.confirm}
        cancelText={I18n.back}
        id="publish_item_modal"
        loading={loading}
        confirmButtonClassName="ubPrimaryButton"
      />

      <ConfirmationModal
        open={stockModal}
        onClose={() => toggleStockModal(false)}
        onConfirm={handleBulkUpdateStatus}
        message={`Would you like to mark the selected items as Out of stock?`}
        confirmText={I18n.confirm}
        cancelText={I18n.back}
        id="stock_out_of_stock_modal"
        loading={loading}
        confirmButtonClassName="ubPrimaryButton"
      />
    </>
  );
};

export default StockList;
